import React, { useState, useEffect } from 'react';
import { companyApi } from '../services/api';
import { CalendarClock, AlertTriangle, CheckCircle2 } from 'lucide-react';

/**
 * SubscriptionBadge — countdown abbonamento azienda (1 anno).
 * Mostra giorni rimanenti con colore secondo la soglia:
 *  - verde > 30 giorni
 *  - ambra 8-30 giorni
 *  - rosso 1-7 giorni (urgenza)
 *  - rosso scuro + "Scaduto" se ≤ 0
 *
 * Visible solo agli utenti azienda (non al super admin).
 */
const SubscriptionBadge = ({ user }) => {
  const [expires, setExpires] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    if (user?.role === 'super_admin' || !user?.company_id) {
      setLoading(false);
      return;
    }
    companyApi.get()
      .then((r) => {
        if (mounted && r.data?.subscription_expires) {
          setExpires(new Date(r.data.subscription_expires));
        }
      })
      .catch(() => { /* silent */ })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [user?.role, user?.company_id]);

  if (loading || !expires) return null;

  const now = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysLeft = Math.ceil((expires.getTime() - now.getTime()) / msPerDay);

  // Determina stile in base ai giorni rimanenti
  let tone, Icon, title, sub;
  if (daysLeft <= 0) {
    tone = 'expired';
    Icon = AlertTriangle;
    title = 'Abbonamento scaduto';
    sub = 'Contatta per rinnovare';
  } else if (daysLeft <= 7) {
    tone = 'urgent';
    Icon = AlertTriangle;
    title = `${daysLeft} giorn${daysLeft === 1 ? 'o' : 'i'} alla scadenza`;
    sub = 'Rinnova subito';
  } else if (daysLeft <= 30) {
    tone = 'warn';
    Icon = CalendarClock;
    title = `${daysLeft} giorni rimanenti`;
    sub = 'Pensa al rinnovo';
  } else {
    tone = 'ok';
    Icon = CheckCircle2;
    title = `${daysLeft} giorni`;
    sub = `Scade il ${expires.toLocaleDateString('it-IT')}`;
  }

  const cls = {
    ok:      'bg-emerald-50 border-emerald-200 text-emerald-800',
    warn:    'bg-amber-50 border-amber-300 text-amber-900',
    urgent:  'bg-orange-50 border-orange-400 text-orange-900 animate-pulse',
    expired: 'bg-red-100 border-red-500 text-red-900 animate-pulse',
  }[tone];

  // Avviso prezzo: appare quando la scadenza è ≤ 60 giorni (incluso scaduto)
  const showPricingNotice = daysLeft <= 60;

  return (
    <div
      data-testid="subscription-badge"
      className={`mb-3 border rounded-sm px-3 py-2 text-xs ${cls}`}
      title={`Scadenza: ${expires.toLocaleDateString('it-IT')}`}
    >
      <div className="flex items-start gap-2">
        <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold leading-tight">{title}</div>
          <div className="text-[10px] opacity-80 leading-tight mt-0.5">{sub}</div>
        </div>
      </div>
      {showPricingNotice && (
        <div
          data-testid="subscription-pricing-notice"
          className="mt-2 pt-2 border-t border-current/20 text-[10px] leading-snug space-y-1 opacity-95"
        >
          <div>
            <span className="font-bold">Rinnovo:</span> il canone passerà da{' '}
            <span className="font-bold">200&nbsp;€</span> a{' '}
            <span className="font-bold">400&nbsp;€</span> annuali. Chi non desidera rinnovare può salvare i documenti entro la scadenza.
          </div>
          <div dir="rtl" lang="ar" data-testid="subscription-pricing-notice-ar" className="font-['system-ui'] text-[10px] leading-snug pt-1 border-t border-current/15">
            سيرتفع الاشتراك من <span className="font-bold">200€</span> إلى <span className="font-bold">400€</span> سنوياً. من لا يرغب في التجديد يمكنه حفظ مستنداته قبل انتهاء الصلاحية.
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionBadge;
